import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Truck, Plus, Search, Edit2, Trash2, X, Zap, Fuel, AlertTriangle, CheckCircle } from "lucide-react";

const FUEL_TYPES = ["Diesel", "GNC", "Eléctrico", "Gasolina", "Híbrido"];
const FAMILIES = ["CRC CARGA LATERAL", "CRC CARGA TRASERA", "BALDEADORA", "BARREDORA MOTORIZADA", "BARREDORA COMPACTA", "BARREDORA GRANDE", "LAVACONTENEDORES", "CAMIÓN GANCHO", "CAJA ABIERTA", "VEHÍCULO INSPECCIÓN", "RECOLECTOR BILATERAL", "OTRO"];
const STATUS_CONFIG = {
  active: { label: "Activo", class: "badge-ok", icon: CheckCircle },
  maintenance: { label: "Mantenimiento", class: "badge-pending", icon: AlertTriangle },
  inactive: { label: "Inactivo", class: "badge-draft", icon: X }
};

export default function Fleet() {
  const { authAxios } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [filterContract, setFilterContract] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFuel, setFilterFuel] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [form, setForm] = useState({ plate: "", brand: "", model_name: "", family: "", subfamily: "", group: "", center: "", contract_id: "", status: "active", fuel_type: "Diesel", registration_date: "" });

  useEffect(() => {
    Promise.all([authAxios.get("/vehicles"), authAxios.get("/contracts")])
      .then(([v, c]) => {
        setVehicles(v.data);
        setContracts(c.data);
        if (c.data.length > 0) setForm(f => ({ ...f, contract_id: c.data[0].id }));
      }).finally(() => setLoading(false));
  }, []);

  const filtered = vehicles.filter(v => {
    const mc = !filterContract || v.contract_id === filterContract;
    const ms = !filterStatus || v.status === filterStatus;
    const mf = !filterFuel || v.fuel_type === filterFuel;
    const mq = !search || v.plate.toLowerCase().includes(search.toLowerCase()) ||
      v.brand?.toLowerCase().includes(search.toLowerCase()) ||
      v.family?.toLowerCase().includes(search.toLowerCase());
    return mc && ms && mf && mq;
  });

  const contractName = id => contracts.find(c => c.id === id)?.name || id;
  const contractColor = id => contracts.find(c => c.id === id)?.color || "#007934";

  const FUEL_ICONS = { "Eléctrico": "bg-emerald-50 text-emerald-600", "GNC": "bg-blue-50 text-blue-600", "Diesel": "bg-amber-50 text-amber-700", "Gasolina": "bg-orange-50 text-orange-700", "Híbrido": "bg-purple-50 text-purple-600" };

  const summaryStats = {
    active: vehicles.filter(v => v.status === "active").length,
    maintenance: vehicles.filter(v => v.status === "maintenance").length,
    electric: vehicles.filter(v => v.fuel_type === "Eléctrico").length,
    gnc: vehicles.filter(v => v.fuel_type === "GNC").length,
  };

  const openNew = () => {
    setEditVehicle(null);
    setForm({ plate: "", brand: "", model_name: "", family: "", subfamily: "", group: "", center: contracts[0]?.city || "", contract_id: contracts[0]?.id || "", status: "active", fuel_type: "Diesel", registration_date: "" });
    setShowModal(true);
  };

  const openEdit = v => { setEditVehicle(v); setForm({ ...v }); setShowModal(true); };

  const handleSave = async () => {
    if (editVehicle) {
      const r = await authAxios.put(`/vehicles/${editVehicle.id}`, form);
      setVehicles(prev => prev.map(v => v.id === editVehicle.id ? r.data : v));
    } else {
      const r = await authAxios.post("/vehicles", form);
      setVehicles(prev => [...prev, r.data]);
    }
    setShowModal(false);
  };

  const handleDelete = async id => {
    if (!window.confirm("¿Eliminar este vehículo?")) return;
    await authAxios.delete(`/vehicles/${id}`);
    setVehicles(prev => prev.filter(v => v.id !== id));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-10 h-10 border-4 border-[#007934] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Maquinaria</h1>
          <p className="text-slate-400 text-sm mt-0.5">{vehicles.length} vehículos en flota · {summaryStats.active} activos</p>
        </div>
        <button
          data-testid="add-vehicle-btn"
          onClick={openNew}
          className="flex items-center gap-2 bg-[#007934] hover:bg-[#005c27] text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-md transition-all active:scale-95"
        >
          <Plus size={16} /> Añadir Vehículo
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-in-up">
        {[
          { label: "Activos", value: summaryStats.active, color: "#007934", bg: "#E6F4EA" },
          { label: "En Mantenimiento", value: summaryStats.maintenance, color: "#D97706", bg: "#FEF3C7" },
          { label: "Eléctricos", value: summaryStats.electric, color: "#3BAD2D", bg: "#E6F4EA" },
          { label: "GNC (Gas)", value: summaryStats.gnc, color: "#0891B2", bg: "#E0F2FE" },
        ].map(s => (
          <div key={s.label} className="card-white p-4">
            <p className="text-xs font-semibold text-slate-400">{s.label}</p>
            <p className="text-2xl font-black mt-1" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 animate-fade-in-up">
        <div className="relative flex-1 min-w-40">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="search-vehicles"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar matrícula, marca..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007934]/20 focus:border-[#007934]"
          />
        </div>
        {[
          { value: filterContract, onChange: setFilterContract, options: [["", "Todos los contratos"], ...contracts.map(c => [c.id, c.name])], testId: "filter-vehicle-contract" },
          { value: filterStatus, onChange: setFilterStatus, options: [["", "Todos los estados"], ["active", "Activo"], ["maintenance", "Mantenimiento"], ["inactive", "Inactivo"]], testId: "filter-vehicle-status" },
          { value: filterFuel, onChange: setFilterFuel, options: [["", "Todos los combustibles"], ...FUEL_TYPES.map(f => [f, f])], testId: "filter-vehicle-fuel" },
        ].map(({ value, onChange, options, testId }) => (
          <select key={testId} data-testid={testId} value={value} onChange={e => onChange(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20">
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
        <span className="text-xs text-slate-400 self-center">{filtered.length} vehículos</span>
      </div>

      {/* Grid/Table toggle */}
      <div className="card-white overflow-hidden animate-fade-in-up">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th>Matrícula</th>
              <th>Marca / Modelo</th>
              <th>Familia</th>
              <th>Contrato</th>
              <th>Combustible</th>
              <th>Estado</th>
              <th>Registro</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(v => {
              const statusCfg = STATUS_CONFIG[v.status] || STATUS_CONFIG.inactive;
              return (
                <tr key={v.id} data-testid={`vehicle-${v.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Truck size={14} className="text-slate-500" />
                      </div>
                      <span className="font-mono font-bold text-slate-800">{v.plate}</span>
                    </div>
                  </td>
                  <td>
                    <p className="font-semibold text-slate-700 text-sm">{v.brand}</p>
                    <p className="text-xs text-slate-400">{v.model_name}</p>
                  </td>
                  <td>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{v.family}</span>
                    {v.subfamily && <p className="text-xs text-slate-400 mt-0.5">{v.subfamily}</p>}
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: contractColor(v.contract_id) }} />
                      <span className="text-sm text-slate-600">{contractName(v.contract_id)}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FUEL_ICONS[v.fuel_type] || "bg-slate-100 text-slate-500"}`}>
                      {v.fuel_type}
                    </span>
                  </td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.class}`}>
                      {statusCfg.label}
                    </span>
                  </td>
                  <td className="text-xs text-slate-400">{v.registration_date || "—"}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(v)} className="text-slate-400 hover:text-[#007934] transition-colors"><Edit2 size={14} /></button>
                      <button onClick={() => handleDelete(v.id)} className="text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Truck size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No se encontraron vehículos</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900">{editVehicle ? "Editar Vehículo" : "Nuevo Vehículo"}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Matrícula", key: "plate", placeholder: "0000 ABC" },
                { label: "Marca", key: "brand", placeholder: "IVECO" },
                { label: "Modelo", key: "model_name", placeholder: "STRALIS GNC" },
                { label: "Centro", key: "center", placeholder: "Barakaldo" },
              ].map(({ label, key, placeholder }) => (
                <div key={key}>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
                  <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20" placeholder={placeholder} />
                </div>
              ))}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Familia</label>
                <select value={form.family} onChange={e => setForm(f => ({ ...f, family: e.target.value }))}
                  className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20">
                  {FAMILIES.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Combustible</label>
                <select value={form.fuel_type} onChange={e => setForm(f => ({ ...f, fuel_type: e.target.value }))}
                  className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20">
                  {FUEL_TYPES.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Contrato</label>
                <select value={form.contract_id} onChange={e => setForm(f => ({ ...f, contract_id: e.target.value }))}
                  className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20">
                  {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Estado</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20">
                  <option value="active">Activo</option>
                  <option value="maintenance">Mantenimiento</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Fecha Matriculación</label>
                <input type="date" value={form.registration_date || ""} onChange={e => setForm(f => ({ ...f, registration_date: e.target.value }))}
                  className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancelar</button>
              <button data-testid="save-vehicle-btn" onClick={handleSave} className="px-5 py-2 text-sm font-semibold bg-[#007934] text-white rounded-lg hover:bg-[#005c27] shadow-md transition-all">
                {editVehicle ? "Actualizar" : "Crear"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
